import type { Option } from '@ksyos/design-system';
import { DateTime } from '@ksyos/date-time';
import {
    Button,
    Card,
    Col,
    Form,
    FormButtonRow,
    FormGroup,
    FormParagraph,
    FormSection,
    IconButton,
    Row,
    Select,
    TextInput,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IDeviceStatus } from '@common/interfaces/deviceStatus';
import type { IDeviceTypeAddedServices } from '@common/interfaces/deviceType';
import type { IOrganization } from '@frontend/components/interfaces';
import { DeviceStatus, PractitionerRoleInRequest } from '@common/enums';
import { DeviceStatusReferenceType } from '@common/enums/deviceStatusReferenceType';
import { getDisplayName } from '@common/utils/getDisplayName';
import { removeDuplicatedObjectsByKey } from '@common/utils/removeDuplicatedObjectsByKey';
import { deviceStatusEditSchema } from '@common/validation/deviceStatus';
import { KsyosPractitionersDropdown } from '@frontend/components/elements/KsyosPractitionersDropdown';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils';
import { enumToDropdownIOptions } from '@frontend/utils/enumToDropdownIOptions';
import type { ValidationErrors } from '../../../utils/validation';
import type { IDevice } from './DeviceDetails';
import type { IDeviceStatusOrganization } from './DevicesList';
import { emptyValidationErrors, validate } from '../../../utils/validation';
import { DevicesDetailsLogbookModal } from './DevicesDetailsLogbookModal';
import { DeviceStatusOrganizationsSelection } from './DeviceStatusOrganizationsSelection';

interface IDeviceDetailsProps {
    device: IDevice;
    reloadDevice: () => Promise<void>;
}

export function DeviceStatusCard(props: IDeviceDetailsProps): JSX.Element | null {
    const statusList: Option[] = enumToDropdownIOptions(DeviceStatus, 'deviceStatusEnum');
    const [deviceStatus, setDeviceStatus] = useState<IDeviceStatus>(props.device.deviceStatus);
    const [savedDeviceStatusOrganizations, setSavedDeviceStatusOrganizations] = useState<
        IDeviceStatusOrganization[] | null
    >(null);
    const [deviceStatusOrganizationIds, setDeviceStatusOrganizationIds] = useState<string[]>([]);
    const [selectedPractitionerName, setSelectedPractitionerName] = useState<string>();
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [organizationOptionsList, setOrganizationOptionsList] = useState<Option[]>([]);
    const [showDeviceDetailsLogbookModal, setShowDeviceDetailsLogbookModal] = useState<boolean>(false);
    const [deviceStatusOrganizationsError, setDeviceStatusOrganizationsError] = useState<string | null>();

    useEffect(() => {
        loadOrganizations({
            consumingServices: props.device.deviceType.consumingServices,
            providingServices: props.device.deviceType.providingServices,
        });
        loadPractitionerName();
    }, []);

    useEffect(() => {
        setDeviceStatus(props.device.deviceStatus);
        setSavedDeviceStatusOrganizations(props.device.deviceStatusOrganizations);
        initializeDeviceStatusOrganizationIds(props.device.deviceStatusOrganizations);
    }, [props.device]);

    useEffect(() => {
        setValidationErrors(emptyValidationErrors);
        if (deviceStatus.status !== DeviceStatus.issued) {
            setDeviceStatusOrganizationIds([]);
            setDeviceStatusOrganizationsError(null);
        }
    }, [deviceStatus.status]);

    useEffect(() => {
        setValidationErrors(validationErrors.remove('organizationIds'));
    }, [deviceStatusOrganizationIds]);

    return (
        <Card>
            <Card.Transition type={isEditing ? 'edit' : 'read'} />
            <Card.Header
                action={
                    !isEditing ? (
                        <IconButton
                            className="automation-device-status-card-button-edit"
                            icon="edit-light"
                            labelText="edit"
                            onClick={() => setIsEditing(true)}
                        />
                    ) : (
                        <IconButton
                            icon="close"
                            labelText="close"
                            onClick={() => handleCancel()}
                        />
                    )
                }
                title={translateKey('common.status')}
            />
            <Card.Body>
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        save();
                    }}
                    submitOnEnter={false}
                >
                    {!isEditing && (
                        <>
                            <FormSection>
                                {renderLabel('common.status', translateKey(`deviceStatusEnum.${deviceStatus.status}`))}
                            </FormSection>
                            <FormSection>{renderFieldsByStatus()}</FormSection>
                        </>
                    )}

                    {isEditing && (
                        <>
                            <FormGroup
                                isRequired={true}
                                label={translateKey('common.status')}
                            >
                                <Select
                                    className="automation-device-status-card-select-status"
                                    defaultValue={statusList.find((item) => item.value === deviceStatus.status)}
                                    name={'device.status'}
                                    onChange={(option) => {
                                        setDeviceStatus({
                                            ...deviceStatus,
                                            status: option?.value as DeviceStatus,
                                            referenceType: getReferenceTypeForStatus(option?.value as DeviceStatus),
                                            reference: null,
                                        });
                                    }}
                                    options={statusList}
                                />
                            </FormGroup>
                            {deviceStatus.status === DeviceStatus.withPatient && (
                                <FormGroup
                                    hasErrors={validationErrors.has('deviceStatus.reference')}
                                    isRequired={true}
                                    label={translateKey('devices.processId')}
                                >
                                    <TextInput
                                        error={getValidationErrorTranslation(
                                            validationErrors.get('deviceStatus.reference')
                                        )}
                                        onChange={(e) => {
                                            setDeviceStatus({
                                                ...deviceStatus,
                                                reference: e.target.value,
                                            });
                                        }}
                                        placeholder={translateKey('devices.processId')}
                                        value={deviceStatus.reference || ''}
                                        variant="outline"
                                    />
                                </FormGroup>
                            )}
                            {deviceStatus.status === DeviceStatus.issued && (
                                <DeviceStatusOrganizationsSelection
                                    className="automation-device-status-card-select-organization"
                                    hasErrors={!!validationErrors?.get('organizationIds')}
                                    onChange={(options: string[]) => {
                                        if (options.toString() !== deviceStatusOrganizationIds.toString()) {
                                            setDeviceStatusOrganizationIds(options);
                                        }
                                    }}
                                    onChangeValidationError={(error?: string) =>
                                        setDeviceStatusOrganizationsError(error ?? null)
                                    }
                                    organizationOptionsList={organizationOptionsList}
                                    selectedDeviceStatusOrganizationIds={deviceStatusOrganizationIds}
                                />
                            )}

                            {deviceStatus.status === DeviceStatus.internalUse && (
                                <FormGroup
                                    hasErrors={validationErrors.has('deviceStatus.reference')}
                                    isRequired={true}
                                    label={translateKey('devices.assistant')}
                                >
                                    <KsyosPractitionersDropdown
                                        className="automation-device-status-card-select-practitioner"
                                        currentPractitionerId={deviceStatus.reference}
                                        errorKey={validationErrors?.get('deviceStatus.reference')}
                                        onChange={(option) => {
                                            setDeviceStatus({
                                                ...deviceStatus,
                                                reference: option?.value ?? null,
                                            });
                                            setSelectedPractitionerName(option?.label);
                                        }}
                                    />
                                </FormGroup>
                            )}

                            {deviceStatus.status === DeviceStatus.sentForRepair &&
                                renderLabel('devices.forRepair', props.device.deviceType.manufacturer)}

                            {deviceStatus.status === DeviceStatus.homeExamination &&
                                renderLabel('deviceTypes.manufacturer', props.device.deviceType.manufacturer)}

                            <FormButtonRow>
                                <Button
                                    isDisabled={isSaving}
                                    onClick={() => handleCancel()}
                                    variant="outline"
                                >
                                    {translateKey('common.cancel')}
                                </Button>
                                <Button
                                    isDisabled={isSaving}
                                    onClick={async () => await save()}
                                >
                                    {translateKey('common.save')}
                                </Button>
                            </FormButtonRow>
                        </>
                    )}
                </Form>
            </Card.Body>
            {!isEditing && renderLogbook()}
        </Card>
    );

    async function save() {
        // eslint-disable-next-line prefer-const
        let { errors, item: deviceStatusUpdateData } = validate(deviceStatusEditSchema, {
            deviceStatus,
            organizationIds: deviceStatusOrganizationIds,
        });

        if (deviceStatusOrganizationsError) {
            errors = errors.set('organizationIds', deviceStatusOrganizationsError);
        }

        setValidationErrors(errors);

        if (errors.size > 0) {
            return;
        }

        setIsSaving(true);

        const result = await fetchJson(`/api/devices/${props.device.id}/status`, 'POST', deviceStatusUpdateData);

        if (result.status === 200) {
            setIsSaving(false);
            setIsEditing(false);
            await props.reloadDevice();
        }
    }

    function handleCancel() {
        setDeviceStatus(props.device.deviceStatus);
        setDeviceStatusOrganizationIds(
            props.device.deviceStatusOrganizations
                ? props.device.deviceStatusOrganizations.map(
                      (deviceStatusOrganization) => deviceStatusOrganization.organization!.id
                  )
                : []
        );
        setValidationErrors(emptyValidationErrors);
        setIsEditing(false);
        loadPractitionerName();
    }

    function renderFieldsByStatus() {
        switch (deviceStatus.status) {
            case DeviceStatus.issued:
                return (
                    <>
                        {savedDeviceStatusOrganizations?.map((deviceStatusOrganization) => {
                            return renderOrganizationLabels(deviceStatusOrganization.organization!);
                        })}
                        {renderLabel(
                            'devices.issueDate',
                            deviceStatus.issueDate ? DateTime.from(deviceStatus.issueDate).format('DD-MM-YYYY') : '-'
                        )}
                    </>
                );
            case DeviceStatus.withPatient:
                return (
                    <>
                        {renderLabel('common.pid', deviceStatus.reference)}
                        {renderLabel(
                            'devices.shipmentDate',
                            deviceStatus.issueDate ? DateTime.from(deviceStatus.issueDate).format('DD-MM-YYYY') : '-'
                        )}
                    </>
                );
            case DeviceStatus.homeExamination:
                return (
                    <>
                        {renderLabel('devices.partner', props.device.deviceType.manufacturer)}
                        {renderLabel(
                            'devices.issueDate',
                            deviceStatus.issueDate ? DateTime.from(deviceStatus.issueDate).format('DD-MM-YYYY') : '-'
                        )}
                    </>
                );
            case DeviceStatus.sentForRepair:
                return (
                    <>
                        {renderLabel('devices.forRepair', props.device.deviceType.manufacturer)}
                        {renderLabel(
                            'devices.shipmentDate',
                            deviceStatus.issueDate ? DateTime.from(deviceStatus.issueDate).format('DD-MM-YYYY') : '-'
                        )}
                    </>
                );
            case DeviceStatus.internalUse:
                return (
                    <>
                        {renderLabel('devices.assistant', selectedPractitionerName)}
                        {renderLabel(
                            'devices.shipmentDate',
                            deviceStatus.issueDate ? DateTime.from(deviceStatus.issueDate).format('DD-MM-YYYY') : '-'
                        )}
                    </>
                );
            default:
                return renderLabel(
                    'devices.issueDate',
                    deviceStatus.issueDate ? DateTime.from(deviceStatus.issueDate).format('DD-MM-YYYY') : '-'
                );
        }
    }

    function renderLabel(label: string, value?: string | null) {
        return (
            <FormGroup label={translateKey(label)}>
                <FormParagraph>{value ?? '-'}</FormParagraph>
            </FormGroup>
        );
    }

    function renderLogbook() {
        return (
            <Card.Footer>
                <Button
                    onClick={() => setShowDeviceDetailsLogbookModal(true)}
                    variant="link"
                >
                    {translateKey('devices.logbook')}
                </Button>
                {showDeviceDetailsLogbookModal && (
                    <DevicesDetailsLogbookModal
                        deviceId={props.device.id}
                        isOpen={showDeviceDetailsLogbookModal}
                        onClose={() => setShowDeviceDetailsLogbookModal(false)}
                    />
                )}
            </Card.Footer>
        );
    }

    function renderOrganizationLabels(organization: IOrganization) {
        return (
            <FormGroup label={translateKey('organization.organization')}>
                <Row
                    columnGap={[2, 4]}
                    rowGap={[2, 4]}
                >
                    <Col span={7}>
                        <FormParagraph>{organization!.name}</FormParagraph>
                    </Col>
                    <Col span={5}>
                        <FormParagraph>{organization!.txcsId}</FormParagraph>
                    </Col>
                </Row>
            </FormGroup>
        );
    }

    async function loadOrganizations(servicesForSelectedDeviceType: IDeviceTypeAddedServices) {
        const providingServiceIds = servicesForSelectedDeviceType.providingServices
            .map((providingService) => providingService.id)
            .join(',');
        const consumingServiceIds = servicesForSelectedDeviceType.consumingServices
            .map((consumingService) => consumingService.id)
            .join(',');

        let consumingServicesOrganizations: Array<IOrganization> = [];
        let providingServicesOrganizations: Array<IOrganization> = [];
        if (consumingServiceIds.length) {
            const result = await fetchJson(
                `/api/serviceConsumersList/${consumingServiceIds}/organizations?&isDeviceOrganizationSelection=true`,
                'GET'
            );
            consumingServicesOrganizations = result.json.items;
        }
        if (providingServiceIds.length) {
            const result = await fetchJson(
                `/api/serviceProvidersList/${providingServiceIds}/organizations?role=${PractitionerRoleInRequest.biometrician}`,
                'GET'
            );
            providingServicesOrganizations = result.json.items;
        }

        const serviceOrganizationsWithDuplicates = [
            ...consumingServicesOrganizations,
            ...providingServicesOrganizations,
        ];
        const serviceOrganizations = removeDuplicatedObjectsByKey(serviceOrganizationsWithDuplicates, 'id');

        setOrganizationOptionsList(
            serviceOrganizations.map((organization) => ({
                value: organization.id,
                label: `${organization.name} ${organization.txcsId}`,
            }))
        );
    }

    function getReferenceTypeForStatus(status: DeviceStatus) {
        switch (status) {
            case DeviceStatus.withPatient:
                return DeviceStatusReferenceType.processId;
            case DeviceStatus.internalUse:
                return DeviceStatusReferenceType.practitionerId;
            default:
                return null;
        }
    }

    function initializeDeviceStatusOrganizationIds(deviceStatusOrganizations: IDeviceStatusOrganization[] | null) {
        setDeviceStatusOrganizationIds(
            deviceStatusOrganizations && deviceStatusOrganizations.length > 0
                ? deviceStatusOrganizations.map((deviceStatusOrganization) => deviceStatusOrganization.organization!.id)
                : []
        );
    }

    async function loadPractitionerName() {
        if (
            props.device.deviceStatus.referenceType === DeviceStatusReferenceType.practitionerId &&
            props.device.deviceStatus.reference
        ) {
            const selectedPractitioner = (
                await fetchJson(`/api/practitioners/${props.device.deviceStatus.reference}`, 'GET')
            ).json;
            setSelectedPractitionerName(getDisplayName(selectedPractitioner, false, 'nameGivenNameFamily'));
        }
    }
}
